/**
 * Per-field source precedence, set by measuring both parsers against 140 live
 * projects. Summary:
 *   stock size  — CAM (.crv MaterialSize), else the g-code stock header,
 *                 else the cut extents rounded up to a whole inch (10 mm)
 *                 with the deepest cut as thickness.
 *   software    — CAM file type first (.crv => Vectric, .art => Carveco, 100%
 *                 exact); the g-code's CAM-software comment header is the
 *                 fallback when no CAM file was dropped.
 *   units       — CAM if it says, else G-code G20/G21. Both 100%.
 *   XY datum    — CAM origin (.crv only), else G-code.
 *   orientation — G-code only (job type, Z-zero, probing, ATC).
 */
import { GcodeExtractedData } from '@/workers/gcodeParser';
import { CamExtractedData } from '@/workers/camTypes';
import { IUnits } from '@/redux/post/post.d';
import { V2File, V2JobDetails } from '../v2Upload.d';
import { detectSoftware, SOFTWARE_BY_FORMAT } from './softwareDetect';
import { asText, JOB_DETAIL_SEED_FIELDS, ParserAnswers } from './autofill';

/** The G-code parse that drives the wizard's fields: the first one whose
 * header states the stock. A project posted for several controllers often has
 * that header in only some files (Buildbotics posts carry none), and the first
 * file's toolpath bbox is a poor stand-in for the board. */
export function primaryGcodeParse(
  results: (GcodeExtractedData | null)[]
): GcodeExtractedData | null {
  return results.find((r) => r?.stock) ?? results[0] ?? null;
}

export const EMPTY_JOB_DETAILS: V2JobDetails = {
  seeded: false,
  width: '',
  height: '',
  thickness: '',
  length: '',
  diameter: '',
  flipDirection: '',
  units: '',
  jobType: '',
  zZeroPosition: '',
  xyDatumPosition: '',
  softwareUsed: [],
  toolSetter: false,
  autoToolChanger: false,
  isDimensionalMaterial: false,
  dimensionalMaterialLinks: [],
};

const str = (v: number | string | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

/** Tolerant equality for a form field's string value against a number the
 * parser produced — the value round-trips through a string input, so "12.25"
 * must still count as equal to 12.25, not just an exact-string match. */
function numericallyEqual(formValue: string, parsed: number): boolean {
  return Math.abs(Number(formValue) - parsed) < 1e-6;
}

/** Whether `jd`'s current job-size fields still equal what `cam`'s own
 * MaterialSize would produce right now — used to decide whether Sign off's
 * "seeded from your design file" badge is still honest after a hand edit.
 * False whenever there's nothing to compare (no CAM file, or a `.art` file,
 * whose thickness-only material cannot vouch for width and height — see
 * `CamMaterial`), so JOB SIZE only ever reads as CAM-sourced while it still
 * says what the CAM file says. */
export function jobSizeMatchesCam(
  jd: Pick<V2JobDetails, 'width' | 'height' | 'thickness'>,
  cam: CamExtractedData | null
): boolean {
  const m = cam?.material;
  if (!m || m.width === null || m.height === null) return false;
  return (
    numericallyEqual(jd.width, m.width) &&
    numericallyEqual(jd.height, m.height) &&
    numericallyEqual(jd.thickness, m.thickness)
  );
}

/** Whether `softwareUsed` still equals exactly the software `cam`'s own
 * format alone would produce. Reuses `SOFTWARE_BY_FORMAT` — the same mapping
 * `mergeJobDetails` uses — so "what the CAM file says" has one definition,
 * not two. */
export function designSwMatchesCam(
  softwareUsed: string[],
  cam: CamExtractedData | null
): boolean {
  if (!cam) return false;
  // Contains, not equals: the seed unions the CAM software with whatever the
  // g-code headers named, so equality would never hold.
  return softwareUsed.includes(SOFTWARE_BY_FORMAT[cam.format]);
}

/** The g-code's stock header, or the smallest whole-unit board its cut fits. */
function boardOf(g: GcodeExtractedData | null): GcodeExtractedData['stock'] {
  const d = g?.dimensions ?? null;
  if (!g || !d || g.stock) return d;
  const step = g.units === IUnits.MM ? 10 : 1;
  const up = (v: number) => Math.ceil(v / step - 1e-9) * step;
  // Deepest cut below a surface zero; the Z range adds the safe height.
  const depth = Math.max(0, -(g.maxCutDepth ?? 0));
  return {
    width: up(d.width),
    height: up(d.height),
    thickness: depth || d.thickness,
  };
}

// fallow-ignore-next-line complexity
export function mergeJobDetails(
  current: V2JobDetails,
  gcode: GcodeExtractedData | null,
  cam: CamExtractedData | null,
  files: V2File[] = []
): V2JobDetails {
  if (!gcode && !cam) return current;

  const stock = cam?.material ?? null;
  const dims = boardOf(gcode);
  // Per field, so a thickness-only .art material keeps the g-code width/height.
  const pick = (k: 'width' | 'height' | 'thickness', fallback: string) =>
    str(stock?.[k] ?? dims?.[k]) || fallback;

  // Every package the files name, not just one: a project drawn in Vectric and
  // posted through Carveco genuinely used both.
  const named = detectSoftware(files, cam, gcode?.camSoftware ?? null);
  const software = named.length ? named : current.softwareUsed;

  return {
    ...current,
    seeded: true,
    width: pick('width', current.width),
    height: pick('height', current.height),
    thickness: pick('thickness', current.thickness),
    units: cam?.units ?? str(gcode?.units) ?? current.units,
    jobType: str(gcode?.jobType) || current.jobType,
    zZeroPosition: str(gcode?.zZeroPosition) || current.zZeroPosition,
    xyDatumPosition:
      str(cam?.xyOrigin) ||
      str(gcode?.xyDatumPosition) ||
      current.xyDatumPosition,
    softwareUsed: software,
    toolSetter: gcode?.hasProbing ?? current.toolSetter,
    autoToolChanger: gcode?.hasATC ?? current.autoToolChanger,
  };
}

/**
 * What the parsers alone would put in Job details — the same merge the seed
 * runs, against an empty form so every value is the file's answer rather than
 * the user's.
 */
export function jobDetailAnswers(
  gcode: GcodeExtractedData | null,
  cam: CamExtractedData | null,
  files: V2File[]
): ParserAnswers {
  const said = mergeJobDetails(EMPTY_JOB_DETAILS, gcode, cam, files);
  const answers: ParserAnswers = Object.fromEntries(
    JOB_DETAIL_SEED_FIELDS.map((key) => [key, asText(said[key])])
  );
  // Both fall back to the form's own value, so without g-code they read "false"
  if (!gcode) {
    answers.toolSetter = '';
    answers.autoToolChanger = '';
  }
  return answers;
}
